import { differenceInDays } from "date-fns";
import { ScheduleLine, FundingMode } from "../types/schema";

export function calculateFundingCap(totalAssetValue: number, fundingLimitPercent: number): number {
  return Math.round((totalAssetValue * fundingLimitPercent) / 100);
}

export interface PVCalculationRow {
  srNo: number;
  fromDate: string;
  dueDate: string;
  factor: number;
  due: number;
  interest: number;
  principal: number;
  principalOS: number;
}

export interface PVCalculationResult {
  calculatedPV: number;
  pvRows: PVCalculationRow[];
}

// Canonical Goal Seek PV calculation using amortization method
export function calculatePresentValueGoalSeek(
  lines: ScheduleLine[],
  lenderFundsGST: boolean,
  discountRatePercent: number,
  assignmentDate: Date,
  lenderFundsTDS: boolean = false
): PVCalculationResult {
  if (!lines.length) {
    return { calculatedPV: 0, pvRows: [] };
  }

  // Sort lines by due date
  const sortedLines = [...lines].sort((a, b) => {
    const dateA = new Date(a.dueDate);
    const dateB = new Date(b.dueDate);
    
    if (isNaN(dateA.getTime())) return 1;
    if (isNaN(dateB.getTime())) return -1;
    
    return dateA.getTime() - dateB.getTime();
  });

  // Validate: Assignment date should be <= first due date
  const firstDueDate = new Date(sortedLines[0].dueDate);
  if (assignmentDate > firstDueDate) {
    console.warn("Assignment date is after first due date, adjusting to first due date");
    assignmentDate = new Date(firstDueDate);
  }

  // Remove duplicate due dates by grouping them
  const groupedLines: ScheduleLine[] = [];
  let currentGroup: ScheduleLine[] = [];
  
  for (const line of sortedLines) {
    if (currentGroup.length === 0 || currentGroup[0].dueDate === line.dueDate) {
      currentGroup.push(line);
    } else {
      // Merge the current group into one line
      const mergedLine = mergeScheduleLines(currentGroup, lenderFundsGST);
      groupedLines.push(mergedLine);
      currentGroup = [line];
    }
  }
  
  // Don't forget the last group
  if (currentGroup.length > 0) {
    const mergedLine = mergeScheduleLines(currentGroup, lenderFundsGST);
    groupedLines.push(mergedLine);
  }

  // Calculate total rental for initial PV guess
  const totalRentals = groupedLines.reduce((sum, line) => {
    const totalGST = line.gstRaw;
    const tdsDeduction = lenderFundsTDS ? line.tdsRounded : 0;
    const due = lenderFundsGST 
      ? line.baseRentRounded - tdsDeduction + totalGST 
      : line.baseRentRounded;
    return sum + due;
  }, 0);

  // Improved Goal Seek bounds and initial guess
  let lowerBound = totalRentals * 0.5;  // More realistic lower bound
  let upperBound = totalRentals * 1.2;  // More realistic upper bound
  let assumedPV = totalRentals * 0.85;  // Better initial guess
  const tolerance = 1;
  const maxIterations = 100;
  
  for (let iteration = 0; iteration < maxIterations; iteration++) {
    const finalPrincipalOS = calculateFinalPrincipalOS(groupedLines, lenderFundsGST, discountRatePercent, assignmentDate, assumedPV, lenderFundsTDS);
    
    // Check convergence
    if (Math.abs(finalPrincipalOS) <= tolerance) {
      break;
    }
    
    // Binary search adjustment
    if (finalPrincipalOS > 0) {
      // Need lower PV to reduce final outstanding
      upperBound = assumedPV;
    } else {
      // Need higher PV to increase final outstanding
      lowerBound = assumedPV;
    }
    
    assumedPV = (lowerBound + upperBound) / 2;
    
    // Prevent infinite loops
    if (Math.abs(upperBound - lowerBound) < 0.01) {
      break;
    }
  }

  // Generate the calculation breakdown table with final PV
  const pvRows: PVCalculationRow[] = [];
  let principalOutstanding = assumedPV;

  groupedLines.forEach((line, index) => {
    const dueDate = new Date(line.dueDate);
    
    // From Date calculation
    let fromDate: Date;
    if (index === 0) {
      fromDate = new Date(assignmentDate);
    } else {
      fromDate = new Date(groupedLines[index - 1].dueDate);
    }
    
    // Factor calculation - ensure positive factors
    let factor: number;
    if (index === 0) {
      factor = Math.max(0, differenceInDays(dueDate, assignmentDate));
    } else {
      const prevDueDate = new Date(groupedLines[index - 1].dueDate);
      factor = Math.max(0, differenceInDays(dueDate, prevDueDate));
      // If factor is 0 (same date), use 1 day to avoid division issues
      if (factor <= 0) factor = 1;
    }
    
    const totalGST = line?.gstRounded;
    const tdsDeduction = lenderFundsTDS ? line.tdsRounded : 0;
    const due = lenderFundsGST 
      ? line.baseRentRounded - tdsDeduction + totalGST 
      : line.baseRentRounded;
    
    // Calculate interest using Principal Outstanding at start of period (actual/365)
    const interest = principalOutstanding * (discountRatePercent / 100) * (factor / 365);
    
    // Principal component = Due amount - Interest
    const principal = due - interest;
    
    // Reduce outstanding principal
    const newPrincipalOS = Math.max(0, principalOutstanding - principal);

    pvRows.push({
      srNo: index + 1,
      fromDate: fromDate?.toLocaleDateString("en-GB"),
      dueDate: dueDate?.toLocaleDateString("en-GB"),
      factor,
      due,
      interest,
      principal,
      principalOS: newPrincipalOS
    });
    
    principalOutstanding = newPrincipalOS;
  });

  return {
    calculatedPV: Math.round(assumedPV),
    pvRows
  };
}

// Helper function to calculate final principal outstanding for a given PV
function calculateFinalPrincipalOS(
  lines: ScheduleLine[],
  lenderFundsGST: boolean,
  discountRatePercent: number,
  assignmentDate: Date,
  assumedPV: number,
  lenderFundsTDS: boolean = false
): number {
  let principalOutstanding = assumedPV;
  
  lines.forEach((line, index) => {
    const dueDate = new Date(line.dueDate);
    
    // Factor calculation - ensure positive factors
    let factor: number;
    if (index === 0) {
      factor = Math.max(0, differenceInDays(dueDate, assignmentDate));
    } else {
      const prevDueDate = new Date(lines[index - 1].dueDate);
      factor = Math.max(0, differenceInDays(dueDate, prevDueDate));
      if (factor <= 0) factor = 1;
    }
    
    const totalGST = line.gstRaw;
    const tdsDeduction = lenderFundsTDS ? line.tdsRounded : 0;
    const due = lenderFundsGST 
      ? line.baseRentRounded - tdsDeduction + totalGST 
      : line.baseRentRounded;
    
    // Calculate interest (actual/365 day count)
    const interest = principalOutstanding * (discountRatePercent / 100) * (factor / 365);
    
    // Principal component
    const principal = due - interest;
    
    // Update outstanding
    principalOutstanding = principalOutstanding - principal;
  });
  
  return principalOutstanding;
}

// Helper function to merge schedule lines with same due date
function mergeScheduleLines(lines: ScheduleLine[], lenderFundsGST: boolean): ScheduleLine {
  if (lines.length === 1) return lines[0];
  
  const merged = { ...lines[0] };
  
  // Sum up all the amounts
  merged.baseRentRounded = lines.reduce((sum, line) => sum + line.baseRentRounded, 0);
  merged.gstRaw = lines.reduce((sum, line) => sum + line.gstRaw, 0);
  merged.totalRaw = lines.reduce((sum, line) => sum + line.totalRaw, 0);
  
  // Use a combined ID
  merged.scheduleLineId = lines.map(l => l.scheduleLineId).join('+');
  
  return merged;
}

// Legacy function for backward compatibility - now uses Goal Seek method
export function calculatePresentValue(
  lines: ScheduleLine[],
  fundingMode: FundingMode,
  discountRatePercent: number,
  assignmentDate: Date
): number {
  // Convert fundingMode to lenderFundsGST boolean
  const lenderFundsGST = fundingMode === "B";
  
  const result = calculatePresentValueGoalSeek(lines, lenderFundsGST, discountRatePercent, assignmentDate);
  return result.calculatedPV;
}

export function calculateVariance(fundingCap: number, pvTotal: number): number {
  return fundingCap - pvTotal;
}

export function selectPeriodsToFitFunding(
  lines: ScheduleLine[],
  lenderFundsGST: boolean,
  discountRatePercent: number,
  assignmentDate: Date,
  fundingCap: number,
  lenderFundsTDS: boolean = false
): string[] {
  // Sort lines by due date (earliest first)
  const sortedLines = [...lines].sort((a, b) => {
    const dateA = new Date(a.dueDate);
    const dateB = new Date(b.dueDate);
    
    // Handle invalid dates by pushing them to the end
    if (isNaN(dateA.getTime())) return 1;
    if (isNaN(dateB.getTime())) return -1;
    
    return dateA.getTime() - dateB.getTime();
  });
  
  const selectedIds: string[] = [];
  let runningPV = 0;
  
  for (const line of sortedLines) {
    // Use Goal Seek method for consistency
    const pvResult = calculatePresentValueGoalSeek([line], lenderFundsGST, discountRatePercent, assignmentDate, lenderFundsTDS);
    const linePV = pvResult.calculatedPV;
    
    if (runningPV + linePV <= fundingCap) {
      selectedIds.push(line.scheduleLineId);
      runningPV += linePV;
    } else {
      break; // Stop when adding the next period would exceed the cap
    }
  }
  
  return selectedIds;
}